if (!customElements.get('lp-header')) {
  class LpHeader extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('[data-drawer]');
      this.hamburger = this.querySelector('.lp-index-header__hamburger');
      this.drawerClose = this.querySelector('.lp-index-header__drawer-close');
      this.searchForm = this.querySelector('.lp-index-header__search--desktop');
      this.searchInput = this.searchForm && this.searchForm.querySelector('.lp-index-header__search-input');
      this.headerRoot = this.closest('.lp-index-header');

      this.onSearchKeydown = (event) => {
        if (event.key === 'Escape') this.closeSearch();
      };
      this.onSearchDocClick = (event) => {
        if (!this.classList.contains('is-search-open')) return;
        const target = event.target;
        if (this.contains(target)) return;
        if (target.closest && target.closest('#boost-sd__search-widget-init-wrapper')) return;
        this.closeSearch();
      };
      this.onSearchResize = () => {
        if (!this.isDesktopViewport()) {
          this.closeSearch();
          return;
        }
        // The drawer only exists below 1024px; left open across the breakpoint
        // it would keep the header inert (setHeaderInert) with no way to close it
        if (this.drawer?.classList.contains('is-open')) this.toggleDrawer(false);
        this.syncBoostWidget();
      };
      this.onSearchScroll = () => this.syncBoostWidget();

      this.querySelectorAll('[data-menu-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => this.toggleDrawer());
      });

      this.querySelectorAll('[data-accordion-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', () => this.toggleAccordion(trigger));
      });

      this.querySelectorAll('[data-rail-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', () => this.toggleRailAccordion(trigger));
      });

      if (this.searchInput) {
        this.searchInput.addEventListener('focus', () => this.openSearch());
      }

      this.querySelectorAll('[data-search-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeSearch());
      });

      this.onDrawerKeydown = (event) => {
        if (!this.drawer.classList.contains('is-open')) return;
        if (event.key === 'Escape') {
          this.toggleDrawer(false);
          return;
        }
        if (event.key === 'Tab') this.trapDrawerFocus(event);
      };

      document.addEventListener('keydown', this.onSearchKeydown);
      document.addEventListener('click', this.onSearchDocClick);
      document.addEventListener('keydown', this.onDrawerKeydown);
      window.addEventListener('resize', this.onSearchResize);
      window.addEventListener('scroll', this.onSearchScroll, { passive: true });
      this.cartLink = this.querySelector('[data-cart-link]');
      this.cartButton = this.querySelector('[data-cart-drawer-trigger]');
      this.onCartButtonClick = () => {
        const smartCart = window.Rebuy?.SmartCart;
        if (typeof smartCart?.show === 'function') smartCart.show();
        else if (this.cartLink) window.location.href = this.cartLink.href;
      };
      this.cartButton?.addEventListener('click', this.onCartButtonClick);

      this.boostWidgetObserver = new MutationObserver(() => {
        this.syncBoostWidget();
        this.fixBoostSearchAccessibility();
        this.syncCartTrigger();
      });
      this.boostWidgetObserver.observe(document.body, { childList: true, subtree: true });
      this.fixBoostSearchAccessibility();
      this.syncCartTrigger();

      // Desktop mega menu (lp-index-header__nav) — its own independent open/
      // close state, kept separate from the drawer's accordion state above.
      this.navItems = this.querySelectorAll('[data-nav-item]');
      this.navOpenTimers = new WeakMap();
      this.navCloseTimers = new WeakMap();

      this.onNavKeydown = (event) => {
        if (event.key === 'Escape') this.closeAllNavItems();
      };
      this.onNavDocClick = (event) => {
        if (this.contains(event.target)) return;
        this.closeAllNavItems();
      };

      document.addEventListener('keydown', this.onNavKeydown);
      document.addEventListener('click', this.onNavDocClick);
      this.setupNav();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onSearchKeydown);
      document.removeEventListener('click', this.onSearchDocClick);
      document.removeEventListener('keydown', this.onDrawerKeydown);
      window.removeEventListener('resize', this.onSearchResize);
      window.removeEventListener('scroll', this.onSearchScroll);
      if (this.boostWidgetObserver) this.boostWidgetObserver.disconnect();

      document.removeEventListener('keydown', this.onNavKeydown);
      this.cartButton?.removeEventListener('click', this.onCartButtonClick);
      document.removeEventListener('click', this.onNavDocClick);
    }

    isDesktopViewport() {
      return window.matchMedia('(min-width: 1024px)').matches;
    }

    isModifiedClick(event) {
      return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
    }

    openSearch() {
      if (!this.isDesktopViewport() || this.classList.contains('is-search-open')) return;
      this.toggleDrawer(false);
      // Open search hides the nav row; the search row takes the header's
      // closed height so the page below does not jump up by that row.
      this.style.setProperty('--lp-index-header-closed-height', this.getBoundingClientRect().height + 'px');
      this.classList.add('is-search-open');
      if (this.headerRoot) this.headerRoot.classList.add('is-search-open');
      document.documentElement.classList.add('lp-index-header-search-open');
      const header = this;
      window.requestAnimationFrame(function () {
        header.syncBoostWidget();
        window.requestAnimationFrame(function () {
          header.syncBoostWidget();
        });
      });
    }

    closeSearch() {
      if (!this.classList.contains('is-search-open')) return;
      this.classList.remove('is-search-open');
      this.style.removeProperty('--lp-index-header-closed-height');
      if (this.headerRoot) this.headerRoot.classList.remove('is-search-open');
      document.documentElement.classList.remove('lp-index-header-search-open');
      document.documentElement.style.removeProperty('--lp-boost-search-left');
      document.documentElement.style.removeProperty('--lp-boost-search-top');
      document.documentElement.style.removeProperty('--lp-boost-search-width');
      if (this.searchInput) this.searchInput.blur();
    }

    syncBoostWidget() {
      if (!this.isDesktopViewport() || !this.classList.contains('is-search-open') || !this.searchForm) return;
      const rect = this.searchForm.getBoundingClientRect();
      const root = document.documentElement;
      root.style.setProperty('--lp-boost-search-left', rect.left + 'px');
      root.style.setProperty('--lp-boost-search-top', rect.bottom + 'px');
      root.style.setProperty('--lp-boost-search-width', rect.width + 'px');
    }

    /**
     * The header renders a cart link and a cart drawer button, one shown at a
     * time. Where Rebuy's cart drawer (#rebuy-cart, a modal dialog) exists, the
     * native button opens it; on pages without it (the /cart page itself) and
     * without JavaScript, the link goes to the cart page. A link that opened a
     * dialog was announced as a link but acted as a button.
     */
    syncCartTrigger() {
      if (!this.cartLink || !this.cartButton) return;
      const useButton = !!document.getElementById('rebuy-cart');
      if (this.cartButton.hidden === !useButton) return;

      // Keep focus on the cart control if it is the one being swapped out
      const outgoing = useButton ? this.cartLink : this.cartButton;
      const incoming = useButton ? this.cartButton : this.cartLink;
      const hadFocus = document.activeElement === outgoing;
      this.cartButton.hidden = !useButton;
      this.cartLink.hidden = useButton;
      if (hadFocus) incoming.focus();
    }

    // Boost's instant search, which takes over these header inputs, adds two
    // things VoiceOver reads out of turn. Its search overlay's Close and Clear
    // buttons carry role="textbox", so they are announced as text fields. Its
    // results status region says "No results found" from page load, before
    // anything was searched, and is read as page content. Both are corrected
    // whenever Boost renders; nothing is written unless it differs.
    fixBoostSearchAccessibility() {
      document.querySelectorAll('.boost-sd__search-bar button[role="textbox"]').forEach((button) => {
        button.removeAttribute('role');
      });

      const status = document.getElementById('boost-sd__isw-live-region');
      if (!status || !status.textContent.trim()) return;
      const searched = [...document.querySelectorAll('.boost-sd__search-widget-init-input')].some((input) => input.value.trim());
      if (!searched) status.textContent = '';
    }

    toggleDrawer(forceOpen) {
      if (!this.drawer) return;
      const willOpen = typeof forceOpen === 'boolean' ? forceOpen : !this.drawer.classList.contains('is-open');
      if (!willOpen && !this.drawer.classList.contains('is-open')) return;

      this.drawer.classList.toggle('is-open', willOpen);
      this.classList.toggle('is-drawer-open', willOpen);
      if (this.hamburger) {
        this.hamburger.setAttribute('aria-expanded', willOpen);
        this.hamburger.classList.toggle('is-open', willOpen);
      }
      document.documentElement.classList.toggle('lp-index-header-drawer-open', willOpen);
      this.setHeaderInert(willOpen);

      if (willOpen) {
        this.drawer.removeAttribute('aria-hidden');
        this.drawer.inert = false;
        // Focus goes straight to the first menu item (HOLIDAY), so VoiceOver
        // users land in the menu instead of swiping past the logo and close
        // button to reach it; the close button is the fallback.
        const firstItem = this.drawer.querySelector(
          '.lp-index-header__drawer-panel a[href], .lp-index-header__drawer-panel button'
        );
        const focusTarget = firstItem || this.drawerClose;
        if (focusTarget) {
          // Force a reflow before focusing: the drawer's own visibility flips
          // from hidden to visible in this same tick, and without a flush the
          // target is still computed as not-focusable at this point.
          void this.drawer.offsetHeight;
          focusTarget.focus();
        }
      } else {
        this.closeAllAccordions();
        if (this.hamburger) this.hamburger.focus();
        // Marks the drawer closed while it is still sliding out, so the modal
        // background guard releases the header now instead of re-inerting the
        // hamburger that has just taken focus. Inert keeps its links out of the
        // Tab order too, before visibility: hidden lands at the end of the slide.
        this.drawer.setAttribute('aria-hidden', 'true');
        this.drawer.inert = true;
      }
    }

    /**
     * VoiceOver swipes never press Tab, so trapDrawerFocus alone let them run
     * from the last menu item out into the page. The drawer is an aria-modal
     * dialog, so the modal background guard (component-modal-background-guard.js)
     * makes the page around the header inert; this covers the drawer's own
     * siblings in the header (logo, search, account, cart), which the guard
     * leaves alone. The overlay stays live: tapping it closes the drawer, and
     * it is aria-hidden already.
     */
    setHeaderInert(inert) {
      for (const child of this.children) {
        if (child === this.drawer || child.classList.contains('lp-index-header__drawer-overlay')) continue;
        child.inert = inert;
      }
    }

    // Keeps Tab/Shift+Tab cycling between the drawer's first and last
    // focusable elements while it's open, so keyboard users can't tab into
    // the page behind the overlay.
    trapDrawerFocus(event) {
      // Collapsed accordion panels are inert, so only count controls that can take focus right now
      const focusable = [
        ...this.drawer.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ),
      ].filter((element) => !element.closest('[inert]') && element.getClientRects().length);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // A panel that is only collapsed with CSS keeps its links in the tab order and
    // the accessibility tree; inert + aria-hidden takes them out until it opens.
    setPanelOpen(panel, open) {
      if (!panel) return;
      panel.toggleAttribute('inert', !open);
      if (open) panel.removeAttribute('aria-hidden');
      else panel.setAttribute('aria-hidden', 'true');
    }

    toggleAccordion(trigger) {
      const item = trigger.closest('[data-accordion-item]');
      if (!item) return;

      const isOpen = item.classList.contains('is-open');
      this.closeAllAccordions();
      if (isOpen) return;

      item.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      this.setPanelOpen(item.querySelector('[data-accordion-panel]'), true);

      // Figma: Mobile / Holiday expanded — the first Level 2 row ("All
      // Holiday") is already expanded when Level 1 opens, not collapsed.
      const firstRailTrigger = item.querySelector('[data-rail-trigger]');
      if (firstRailTrigger) this.toggleRailAccordion(firstRailTrigger, true);
    }

    closeAllAccordions() {
      this.querySelectorAll('[data-accordion-item]').forEach((item) => {
        item.classList.remove('is-open');
        const trigger = item.querySelector('[data-accordion-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        this.setPanelOpen(item.querySelector('[data-accordion-panel]'), false);
      });
      this.closeAllRailAccordions();
    }

    toggleRailAccordion(trigger, forceOpen) {
      const item = trigger.closest('[data-rail-item]');
      const parentList = trigger.closest('[data-accordion-panel]');
      if (!item || !parentList) return;

      const isOpen = item.classList.contains('is-open');
      const willOpen = typeof forceOpen === 'boolean' ? forceOpen : !isOpen;

      this.closeAllRailAccordions(parentList);
      if (!willOpen) return;

      item.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      this.setPanelOpen(item.querySelector('.lp-index-header__drawer-rail-panel'), true);
    }

    // With no `scope`, resets every Level 2 row in the drawer (used when
    // Level 1 switches or the whole drawer closes). With `scope` (a single
    // Level 1 panel), resets only that panel's own Level 2 rows, so
    // switching rows within one category doesn't touch any other category.
    closeAllRailAccordions(scope) {
      const root = scope || this;
      root.querySelectorAll('[data-rail-item]').forEach((item) => {
        item.classList.remove('is-open');
        const trigger = item.querySelector('[data-rail-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        this.setPanelOpen(item.querySelector('.lp-index-header__drawer-rail-panel'), false);
      });
    }

    // ---- lp-index-header__nav (desktop mega menu) ----
    setupNav() {
      this.navItems.forEach((item) => {
        const trigger = item.querySelector('[data-nav-trigger]');
        if (!trigger) return;

        trigger.addEventListener('click', (event) => {
          if (!this.isDesktopViewport() || this.isModifiedClick(event)) return;
          // Hover already opened the panel (or a previous click did) — let a
          // second click follow the link instead of only ever re-toggling it.
          if (item.classList.contains('is-open')) return;
          event.preventDefault();
          this.openNavItem(item);
        });

        item.addEventListener('mouseenter', () => this.scheduleNavOpen(item));
        item.addEventListener('mouseleave', () => this.scheduleNavClose(item));

        item.querySelectorAll('[data-mega-rail-item]').forEach((rail) => {
          rail.addEventListener('click', (event) => {
            if (this.isModifiedClick(event)) return;
            // Already the active category — let the click follow the link.
            if (rail.classList.contains('is-active')) return;
            event.preventDefault();
            this.activateRail(item, rail);
          });
          rail.addEventListener('mouseenter', () => this.activateRail(item, rail));
        });
      });
    }

    scheduleNavOpen(item) {
      if (!this.isDesktopViewport()) return;
      clearTimeout(this.navCloseTimers.get(item));
      this.navOpenTimers.set(item, setTimeout(() => this.openNavItem(item), 60));
    }

    scheduleNavClose(item) {
      clearTimeout(this.navOpenTimers.get(item));
      this.navCloseTimers.set(item, setTimeout(() => this.closeNavItem(item), 200));
    }

    openNavItem(item) {
      this.navItems.forEach((other) => {
        if (other !== item) this.closeNavItem(other);
      });

      const trigger = item.querySelector('[data-nav-trigger]');
      const panel = item.querySelector('[data-mega-panel]');
      if (!panel) return;

      item.classList.add('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
    }

    closeNavItem(item) {
      const trigger = item.querySelector('[data-nav-trigger]');
      const panel = item.querySelector('[data-mega-panel]');

      item.classList.remove('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
    }

    closeAllNavItems() {
      this.navItems.forEach((item) => this.closeNavItem(item));
    }

    activateRail(item, railLink) {
      const index = railLink.dataset.index;

      item.querySelectorAll('[data-mega-rail-item]').forEach((rail) => {
        const isActive = (rail === railLink);
        rail.classList.toggle('is-active', isActive);
        if (isActive) {
          rail.setAttribute('aria-current', 'true');
        } else {
          rail.removeAttribute('aria-current');
        }
      });

      item.querySelectorAll('[data-links-panel]').forEach((panel) => {
        panel.hidden = (panel.dataset.index !== index);
      });
    }
  }

  customElements.define('lp-header', LpHeader);
}
